/**
 * Copyright IBM Corp. 2022, 2026
 */

export const hashicorpDestinations: string[] = [
	'app.terraform.io',
	'boundaryproject.io',
	'cloud.hashicorp.com',
	'consul.io',
	'developer.hashicorp.com',
	'hashicorp.com',
	'nomadproject.io',
	'packer.io',
	'portal.cloud.hashicorp.com',
	'registry.terraform.io',
	'terraform.io',
	'vagrantup.com',
	'vaultproject.io',
	'waypointproject.io',
] as const

const containsHashiCorpDestination = (str: string): boolean =>
	hashicorpDestinations.some(function (destination) {
		return str.indexOf(destination) >= 0
	})

/**
 * This function determines if a `url` is a HashiCorp link (relative URLs, hashes,
 * or a url with a HashiCorp domain) or a  non-HashiCorp link.
 *
 * @param {string | undefined} url - The URL to be parsed. It may be a relative URL, an anchor, or a full external/internal URL.
 *
 * @returns {{ linkType: 'inbound' | 'outbound', href: string }}
 *   An object with two properties:
 *     - `linkType`: Indicates whether the URL is 'inbound' or 'outbound'. Can be used to conditionally
 * 		     render the value of the target attribute.
 *     - `href`: The parsed href (may include pathname, search, and hash for internal URLs).
 *
 * @throws {Error} Throws an error if the URL is undefined or invalid.
 */
export function parseUrl(url: string | undefined): {
	linkType: 'inbound' | 'outbound' | ''
	href: string
} {
	if (!url) {
		return { linkType: '', href: '' }
	}

	// Allow safe relative paths and in-page anchors as-is.
	if (isRelative(url) || isAnchor(url)) {
		return { linkType: 'inbound', href: url }
	}

	// For absolute URLs, ensure they use an allowed scheme and then classify as internal/outbound.
	try {
		const parsed = new URL(url)

		if (!isAllowedScheme(parsed.protocol)) {
			console.log(`Disallowed URL scheme in href: ${url}`)
			return { linkType: '', href: '' }
		}

		if (isInternal(url)) {
			const { pathname, search, hash } = parsed
			const href = pathname + search + hash
			return { linkType: 'inbound', href }
		}

		// Outbound but still using an allowed scheme.
		return { linkType: 'outbound', href: url }
	} catch (error) {
		console.log(`Invalid href: ${url}`)
		return { linkType: '', href: '' }
	}
}

const isRelative = (url: string) => url.startsWith('/')
const isAnchor = (url: string) => url.startsWith('#')

/**
 * Determines whether the provided url uses hashicorp.com or www.hashicorp.com as the hostname.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} - Returns true if the link uses a HashiCorp domain, otherwise false.
 * Returns true if the given protocol string is an allowed URL scheme for links.
 * Only http and https are allowed to prevent injection of javascript:, data:, etc.
 */
const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:']
const isAllowedScheme = (protocol: string): boolean => {
	return ALLOWED_SCHEMES.includes(protocol)
}

/**
 */

export const isInternal = (url: string): boolean => {
	const hostname = new URL(url).hostname
	return hostname === 'hashicorp.com' || hostname === 'www.hashicorp.com'
}

/**
 * Determines whether the provided url uses a HashiCorp hostname. This is used in several
 * components to open a new tab for non-HashiCorp links.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} - Returns true if the link uses a HashiCorp domain, otherwise false.
 */

export const hasHashiCorpDestination = (url: string): boolean => {
	// Fast return in case a relative URL or anchor is passed in
	if (isRelative(url) || isAnchor(url)) return true
	try {
		const hostname = new URL(url).hostname
		const isHashiCorpDestination = containsHashiCorpDestination(hostname)
		return isHashiCorpDestination
	} catch (e) {
		console.error('Error: `url` is not a valid URL', e)
		return false
	}
}
